import * as React from 'react';
import {
  getCoreRowModel,
  useReactTable,
  type Row,
  type RowSelectionState,
} from '@tanstack/react-table';
import { useTableVirtualizer } from '@fragments-sdk/ui';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import {
  FINDINGS_COLUMNS,
  type FindingsRowStaticContextValue,
} from '../findings-table';
import { isFixable, topCategoryItems } from '../findings-display-model';
import type {
  FindingsDecisionActionability,
  FindingsDecisionKind,
  FindingsDecisionPolicyStatus,
  FindingsGroupBy,
  FindingsSearch,
  FindingsSeverity,
  FindingsStatusFilter,
  FindingsView,
} from '../findings-url';
import type { FindingDoc } from '../findings-types';
import type { useFindingsData } from '../use-findings-data';
import type { FixableCategoryBatch } from './repository-bulk-action-bar';
import {
  groupKeyFor,
  groupLabelFor,
  type EnrichedGroup,
} from './repository-groups';
import type { RepositoryVirtualItem } from './repository-table';

type FindingsPage = ReturnType<typeof useFindingsData>['findingsPage'];
type GroupAggregate = ReturnType<typeof useFindingsData>['groupAggregate'];

type RepositoryTableModelArgs = {
  selectedProjectId?: string;
  componentRegistryLoading: boolean;
  groupBy: FindingsGroupBy;
  activeFilter: FindingsStatusFilter;
  activeView: FindingsView;
  activeRule: string | null;
  activeCode: string | null;
  activeCategory: string | null;
  activeSeverity: FindingsSeverity | null;
  activeTokenFilter: string | null;
  activeAreaFilter: string | null;
  activeDecisionActionability: FindingsDecisionActionability | null;
  activeDecisionPolicyStatus: FindingsDecisionPolicyStatus | null;
  activeDecisionKind: FindingsDecisionKind | null;
  activeDecisionKey: string | null;
  sourceFilter?: Exclude<FindingsView, 'all'>;
  componentFilterArg?: string;
  searchQuery: string;
  sortedFindings: FindingDoc[];
  findingsPage: FindingsPage;
  groupAggregate: GroupAggregate;
  pageIndex: number;
  currentPage: number;
  pageSize: number;
  directAutofixLimit: number;
  reviewAutofixLimit: number;
  reviewAutofixFileLimit: number;
  canCreateAutofix: boolean;
  fixPlanOpen: boolean;
  fixPlanCategory: string | null;
  setFixPlanOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFixPlanCategory: React.Dispatch<React.SetStateAction<string | null>>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  updateSearch: (patch: Partial<FindingsSearch>) => void;
};

export function useRepositoryTableModel({
  selectedProjectId,
  componentRegistryLoading,
  groupBy,
  activeFilter,
  activeView,
  activeRule,
  activeCode,
  activeCategory,
  activeSeverity,
  activeTokenFilter,
  activeAreaFilter,
  activeDecisionActionability,
  activeDecisionPolicyStatus,
  activeDecisionKind,
  activeDecisionKey,
  sourceFilter,
  componentFilterArg,
  searchQuery,
  sortedFindings,
  findingsPage,
  groupAggregate,
  pageIndex,
  currentPage,
  pageSize,
  directAutofixLimit,
  reviewAutofixLimit,
  reviewAutofixFileLimit,
  canCreateAutofix,
  fixPlanOpen,
  fixPlanCategory,
  setFixPlanOpen,
  setFixPlanCategory,
  contentRef,
  updateSearch,
}: RepositoryTableModelArgs) {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const lastClickedIndex = React.useRef<number | null>(null);
  const skipNextCheckedChange = React.useRef(false);
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    () => new Set(),
  );

  function toggleGroupExpanded(key: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  React.useEffect(() => {
    setRowSelection({});
    lastClickedIndex.current = null;
  }, [
    activeFilter,
    activeView,
    activeRule,
    activeCategory,
    activeSeverity,
    activeDecisionActionability,
    activeDecisionPolicyStatus,
    activeDecisionKind,
    activeDecisionKey,
    searchQuery,
  ]);

  React.useEffect(() => {
    if (groupBy === 'decision' && activeDecisionKey) {
      setExpandedGroups(new Set([activeDecisionKey]));
      return;
    }
    setExpandedGroups(new Set());
  }, [
    groupBy,
    activeDecisionActionability,
    activeDecisionPolicyStatus,
    activeDecisionKind,
    activeDecisionKey,
  ]);

  const expandedGroupKeys = React.useMemo(
    () => Array.from(expandedGroups),
    [expandedGroups],
  );

  const groupChildrenArgs =
    selectedProjectId &&
    !componentRegistryLoading &&
    groupBy !== 'none' &&
    expandedGroupKeys.length > 0
      ? {
          projectId: selectedProjectId as any,
          groupBy,
          groupKeys: expandedGroupKeys,
          status: activeFilter === 'all' ? undefined : activeFilter,
          source: sourceFilter,
          tokenFilter: activeTokenFilter ?? undefined,
          area: activeAreaFilter ?? undefined,
          code: activeCode ?? undefined,
          ruleId: activeRule ?? undefined,
          category: activeCategory ?? undefined,
          severity: activeSeverity ?? undefined,
          searchQuery: searchQuery || undefined,
          filePath: componentFilterArg,
          actionability:
            groupBy === 'decision'
              ? (activeDecisionActionability ?? undefined)
              : undefined,
          policyStatus:
            groupBy === 'decision'
              ? (activeDecisionPolicyStatus ?? undefined)
              : undefined,
          decisionKind:
            groupBy === 'decision'
              ? (activeDecisionKind ?? undefined)
              : undefined,
          decisionKey:
            groupBy === 'decision'
              ? (activeDecisionKey ?? undefined)
              : undefined,
          limitPerGroup: 2000,
        }
      : ('skip' as const);

  const groupChildren = useQuery(
    api.findingsGroups.getGroupChildren,
    groupChildrenArgs,
  );
  const groupChildrenLoading =
    groupChildrenArgs !== 'skip' && groupChildren === undefined;
  const groupedChildFindings = React.useMemo<FindingDoc[]>(() => {
    if (!groupChildren) return [];
    return groupChildren.groups.flatMap(
      (group) => group.findings as unknown as FindingDoc[],
    );
  }, [groupChildren]);

  const selectionRefs = React.useMemo<
    FindingsRowStaticContextValue['selection']
  >(
    () => ({
      setRowSelection,
      lastClickedIndexRef: lastClickedIndex,
      skipNextCheckedChangeRef: skipNextCheckedChange,
    }),
    [],
  );

  const tableState = React.useMemo(() => ({ rowSelection }), [rowSelection]);
  const tableFindings = React.useMemo(() => {
    if (groupBy === 'none' || groupedChildFindings.length === 0) {
      return sortedFindings;
    }
    const seen = new Set<string>();
    const merged: FindingDoc[] = [];
    for (const finding of [...groupedChildFindings, ...sortedFindings]) {
      if (seen.has(finding._id)) continue;
      seen.add(finding._id);
      merged.push(finding);
    }
    return merged;
  }, [groupBy, groupedChildFindings, sortedFindings]);

  const table = useReactTable({
    data: tableFindings,
    columns: FINDINGS_COLUMNS,
    state: tableState,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row._id,
    enableRowSelection: true,
  });

  const selectedDerived = React.useMemo(() => {
    const rows = table.getSelectedRowModel().rows;
    const ids: string[] = [];
    const openIds: string[] = [];
    const fixableRows: typeof rows = [];
    const fixableIds: string[] = [];
    const fixableFiles = new Set<string>();
    const resolvedOrIgnoredIds: string[] = [];
    let historicalCount = 0;
    let manualCount = 0;
    for (const row of rows) {
      const finding = row.original;
      ids.push(finding._id);
      if (finding.status === 'open') {
        openIds.push(finding._id);
        if (isFixable(finding)) {
          fixableRows.push(row);
          fixableIds.push(finding._id);
          if (finding.filePath) fixableFiles.add(finding.filePath);
        } else {
          manualCount += 1;
        }
      } else {
        historicalCount += 1;
        resolvedOrIgnoredIds.push(finding._id);
      }
    }
    return {
      rows,
      ids,
      openIds,
      fixableRows,
      fixableIds,
      manualCount,
      historicalCount,
      resolvedOrIgnoredIds,
      fixableFileCount: fixableFiles.size,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, tableFindings]);

  const selectedFixableCategoryBatches = React.useMemo<
    FixableCategoryBatch[]
  >(() => {
    const batches = new Map<
      string,
      {
        category: string;
        count: number;
        fileCount: number;
        ids: string[];
        files: Set<string>;
      }
    >();
    for (const row of selectedDerived.fixableRows) {
      const category = row.original.category ?? 'other';
      const existing =
        batches.get(category) ??
        ({
          category,
          count: 0,
          fileCount: 0,
          ids: [],
          files: new Set<string>(),
        } satisfies {
          category: string;
          count: number;
          fileCount: number;
          ids: string[];
          files: Set<string>;
        });
      existing.count += 1;
      existing.ids.push(row.original._id);
      if (row.original.filePath) existing.files.add(row.original.filePath);
      batches.set(category, existing);
    }

    return Array.from(batches.values())
      .map(({ files, ...batch }) => ({ ...batch, fileCount: files.size }))
      .sort((a, b) => b.count - a.count);
  }, [selectedDerived.fixableRows]);

  const selectedFindings = React.useMemo(
    () => selectedDerived.rows.map((row) => row.original),
    [selectedDerived.rows],
  );
  const selectedCategoryBatch =
    selectedFixableCategoryBatches.find(
      (batch) => batch.category === fixPlanCategory,
    ) ?? null;
  const plannedFixIds = selectedCategoryBatch?.ids ?? [];
  const plannedFixCount = plannedFixIds.length;
  const plannedFixFileCount = selectedCategoryBatch?.fileCount ?? 0;
  const canCreatePlannedFix =
    canCreateAutofix &&
    plannedFixCount > 0 &&
    plannedFixCount <= reviewAutofixLimit &&
    plannedFixFileCount <= reviewAutofixFileLimit;
  const selectedFixPlanMode: 'direct' | 'review' | 'batch' =
    selectedDerived.fixableIds.length <= directAutofixLimit
      ? 'direct'
      : selectedDerived.fixableIds.length <= reviewAutofixLimit
        ? 'review'
        : 'batch';
  const fixPlanVisible = fixPlanOpen && selectedDerived.fixableIds.length > 0;
  const selectedSummaryParts = [
    selectedDerived.openIds.length > 0
      ? `${selectedDerived.openIds.length} current`
      : undefined,
    selectedDerived.historicalCount > 0
      ? `${selectedDerived.historicalCount} historical`
      : undefined,
    selectedDerived.fixableIds.length > 0
      ? `${selectedDerived.fixableIds.length} safe fix${selectedDerived.fixableIds.length === 1 ? '' : 'es'}${selectedFixPlanMode === 'batch' ? ` across ${selectedDerived.fixableFileCount} files` : ''}`
      : undefined,
    selectedDerived.manualCount > 0
      ? `${selectedDerived.manualCount} review required`
      : undefined,
  ].filter(Boolean);

  React.useEffect(() => {
    if (
      selectedDerived.ids.length === 0 ||
      selectedDerived.fixableIds.length === 0
    ) {
      setFixPlanOpen(false);
      setFixPlanCategory(null);
    }
  }, [
    selectedDerived.ids.length,
    selectedDerived.fixableIds.length,
    setFixPlanOpen,
    setFixPlanCategory,
  ]);

  React.useEffect(() => {
    if (
      fixPlanCategory &&
      !selectedFixableCategoryBatches.some(
        (batch) => batch.category === fixPlanCategory,
      )
    ) {
      setFixPlanCategory(null);
    }
  }, [fixPlanCategory, selectedFixableCategoryBatches, setFixPlanCategory]);

  const totalPaginatedItems =
    groupBy === 'none'
      ? (findingsPage?.totalMatching ?? 0)
      : (groupAggregate?.groups.length ?? 0);
  const paginationTotalReady =
    groupBy === 'none'
      ? findingsPage !== undefined
      : groupAggregate !== undefined;
  const totalPages = Math.max(1, Math.ceil(totalPaginatedItems / pageSize));
  const pageStart = totalPaginatedItems === 0 ? 0 : pageIndex * pageSize + 1;
  const pageEnd = Math.min(totalPaginatedItems, (pageIndex + 1) * pageSize);

  const visibleTableGroups = React.useMemo<EnrichedGroup[] | null>(() => {
    if (groupBy === 'none') return null;
    if (!groupAggregate) return null;
    const start = pageIndex * pageSize;
    const visibleGroups = groupAggregate.groups.slice(start, start + pageSize);
    if (visibleGroups.length === 0) return [];

    const wantedKeys = new Set(visibleGroups.map((group) => group.key));
    const rowsByKey = new Map<string, Row<FindingDoc>[]>();
    for (const row of table.getRowModel().rows) {
      const key = groupKeyFor(row.original, groupBy);
      if (!wantedKeys.has(key)) continue;
      const list = rowsByKey.get(key);
      if (list) list.push(row);
      else rowsByKey.set(key, [row]);
    }

    return visibleGroups.map((group) => ({
      key: group.key,
      label:
        (groupBy === 'decision' || groupBy === 'area') &&
        typeof group.label === 'string'
          ? group.label
          : groupLabelFor(group.key, groupBy),
      rows: rowsByKey.get(group.key) ?? [],
      totalCount: group.total,
      openCount: group.open,
      fixableCount: group.fixable,
      openIds: group.openIds as string[],
      fixableIds: group.fixableIds as string[],
      severityCounts: {
        error: group.sevError,
        warning: group.sevWarning,
        info: group.sevInfo,
      },
      categoryItems: topCategoryItems(group.byCategory ?? {}),
      topFiles: group.topFiles ?? [],
      repoRef: group.repoRef,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, groupAggregate, pageIndex, pageSize, tableFindings]);

  React.useEffect(() => {
    if (!paginationTotalReady) return;
    if (currentPage <= totalPages) return;
    updateSearch({ page: totalPages > 1 ? totalPages : undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginationTotalReady, totalPages]);

  const virtualItems = React.useMemo<RepositoryVirtualItem[]>(() => {
    const rows = table.getRowModel().rows;
    if (!visibleTableGroups) {
      return rows.map((row, index) => ({
        kind: 'row' as const,
        row,
        band: index % 2 === 0 ? ('default' as const) : ('alt' as const),
      }));
    }
    const items: RepositoryVirtualItem[] = [];
    for (const [index, group] of visibleTableGroups.entries()) {
      const collapsed = !expandedGroups.has(group.key);
      const band = index % 2 === 0 ? ('alt' as const) : ('default' as const);
      items.push({ kind: 'group', group, collapsed, band });
      if (!collapsed) {
        for (const row of group.rows) {
          items.push({ kind: 'row', row, band });
        }
      }
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTableGroups, expandedGroups, tableFindings]);

  // Row virtualization comes from the shared design-system primitive
  // (@fragments-sdk/ui). The heterogeneous group/row stream + lazy children
  // stay app-specific here; only the windowing math lives in the primitive.
  const { virtualRows, paddingTop, paddingBottom, measureElement } =
    useTableVirtualizer<RepositoryVirtualItem>({
      items: virtualItems,
      getScrollElement: () => contentRef.current,
      estimateSize: (item) => (item.kind === 'group' ? 52 : 64),
      getItemKey: (item) =>
        item.kind === 'group' ? `g:${item.group.key}` : `r:${item.row.id}`,
      overscan: 12,
    });

  const safeFixIds = React.useMemo(() => {
    if (groupAggregate) {
      return Array.from(
        new Set(groupAggregate.groups.flatMap((group) => group.fixableIds)),
      ) as string[];
    }
    return sortedFindings
      .filter((finding) => finding.status === 'open' && isFixable(finding))
      .map((finding) => finding._id);
  }, [groupAggregate, sortedFindings]);

  return {
    rowSelection,
    setRowSelection,
    expandedGroups,
    toggleGroupExpanded,
    groupChildrenLoading,
    selectionRefs,
    table,
    selectedIds: selectedDerived.ids,
    selectedOpenIds: selectedDerived.openIds,
    selectedFixableIds: selectedDerived.fixableIds,
    selectedResolvedOrIgnoredIds: selectedDerived.resolvedOrIgnoredIds,
    selectedFindings,
    selectedFixPlanMode,
    selectedFixableCategoryBatches,
    selectedCategoryBatch,
    plannedFixIds,
    plannedFixCount,
    plannedFixFileCount,
    canCreatePlannedFix,
    fixPlanVisible,
    selectedSummaryParts,
    totalPaginatedItems,
    totalPages,
    pageStart,
    pageEnd,
    virtualRows,
    paddingTop,
    paddingBottom,
    measureRow: measureElement,
    safeFixIds,
  };
}
